/**
 * Codificación de GeoJSON y texto en español en mapas
 *
 * CAUSA MÁS PROBABLE DE "SantibÃ¡Ã±ez" EN LUGAR DE "Santibáñez":
 * - El archivo GeoJSON está guardado en UTF-8 (ej. "á" = bytes 0xC3 0xA1).
 * - Al cargar con fetch() sin controlar la codificación, el navegador (o el servidor)
 *   puede interpretar los bytes como Latin-1/Windows-1252: 0xC3 → "Ã", 0xA1 → "¡".
 * - Resultado: "Ã¡" en lugar de "á", "Ã±" en lugar de "ñ", etc. (mojibake).
 *
 * SOLUCIÓN RECOMENDADA (en este orden):
 * 1. Guardar los .geojson en UTF-8 sin BOM (origen de la verdad).
 * 2. Al cargar con fetch, no usar response.json(); usar arrayBuffer() y decodificar
 *    explícitamente con TextDecoder('utf-8') para que no se interprete como otra codificación.
 * 3. Si el archivo viene en Windows-1252 (legacy), decodificar con TextDecoder('windows-1252').
 * 4. Como red de seguridad: normalizar strings ya corruptos con fixMojibakeUtf8AsLatin1().
 *
 * CÓMO VERIFICAR/CONFIGURAR LA CODIFICACIÓN DEL ARCHIVO:
 * - En VS Code: barra inferior derecha muestra la codificación (UTF-8, Windows 1252, etc.).
 *   Clic → "Save with Encoding" → elegir "UTF-8" (sin BOM).
 * - En el servidor: que las respuestas incluyan header Content-Type: application/json; charset=utf-8.
 */

const UTF8_DECODER = new TextDecoder('utf-8')
const LATIN1_DECODER = new TextDecoder('windows-1252')

/**
 * Detecta si un string parece mojibake típico (UTF-8 leído como Latin-1):
 * secuencia "Ã" (U+00C3) seguida de un byte interpretado como carácter.
 */
function looksLikeMojibake(str: string): boolean {
  return /\u00C3[\u00A1\u00A9\u00AD\u00B1\u00B3\u00BA\u0081\u0089\u008D\u0091\u0093\u009A]/.test(str)
}

/**
 * Carga GeoJSON desde una URL controlando la codificación.
 * 1. Lee la respuesta como arrayBuffer (bytes crudos).
 * 2. Prueba UTF-8; si el resultado contiene caracteres de reemplazo () o mojibake, prueba Windows-1252.
 * 3. Parsea el JSON con la cadena decodificada.
 */
export async function fetchGeoJsonWithEncoding(url: string): Promise<any> {
  const res = await fetch(url)
  const buffer = await res.arrayBuffer()

  const asUtf8 = UTF8_DECODER.decode(buffer)
  const hasReplacement = asUtf8.includes('\uFFFD')
  const hasMojibake = looksLikeMojibake(asUtf8)

  const text = hasReplacement || hasMojibake ? LATIN1_DECODER.decode(buffer) : asUtf8
  return JSON.parse(text)
}

/**
 * Corrige strings que ya están corruptos (UTF-8 interpretado como Latin-1).
 * Útil como red de seguridad al mostrar propiedades en popups/paneles.
 * Pares típicos: Ã¡→á, Ã±→ñ, Ã³→ó, Ã­→í, Ã©→é, Ãº→ú y mayúsculas.
 */
export function fixMojibakeUtf8AsLatin1(text: string): string {
  if (typeof text !== 'string') return text
  return text
    .replace(/\uFFFD/g, 'ó')
    .replace(/ï¿½/g, 'ó')
    .replace(/\u00C3\u00B1/g, 'ñ')
    .replace(/\u00C3\u00A1/g, 'á')
    .replace(/\u00C3\u00B3/g, 'ó')
    .replace(/\u00C3\u00AD/g, 'í')
    .replace(/\u00C3\u00A9/g, 'é')
    .replace(/\u00C3\u00BA/g, 'ú')
    .replace(/\u00C3\u0091/g, 'Ñ')
    .replace(/\u00C3\u0081/g, 'Á')
    .replace(/\u00C3\u0093/g, 'Ó')
    .replace(/\u00C3\u008D/g, 'Í')
    .replace(/\u00C3\u0089/g, 'É')
    .replace(/\u00C3\u009A/g, 'Ú')
}

/**
 * Recorre un GeoJSON y normaliza los valores string de feature.properties.
 * Útil si quieres corregir una sola vez al cargar en lugar de en cada popup.
 */
export function normalizeGeoJsonTextProperties(geojson: any): any {
  if (!geojson || geojson.type !== 'FeatureCollection') return geojson
  const fc = geojson
  const features = (fc.features || []).map((f: any) => {
    if (!f.properties || typeof f.properties !== 'object') return f
    const fixed: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(f.properties)) {
      fixed[k] = typeof v === 'string' ? fixMojibakeUtf8AsLatin1(v) : v
    }
    return { ...f, properties: fixed }
  })
  return { ...fc, features } as any
}
